/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import {
  concatStyleSets,
  IDetailsColumnStyles,
  IDetailsHeaderStyles,
  IDetailsListStyles,
  IDetailsRowStyles,
  IGroupHeaderStyles,
} from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontWeights, Sizes } from '../../themes/styles';

export enum DetailsListSizes {
  GroupPaddingLeftRight = 32,
  GroupPaddingBottom = 24,
  GroupPaddingContentsBottom = 8,
  ContentsMarginBottom = 16,
  GroupPageMarginBottom = 8,
  GroupHeaderExpanderWidth = 36,
  CellLeftPadding = 24,
  StatusCellLeftPadding = 32,
  GroupButtonLeftPadding = 66,
  GroupButtonRightPadding = 70,
}

export const detailsListStylesDefault: Partial<IDetailsListStyles> = {
  root: {
    overflow: 'visible',
  },
};

export const detailsListStylesList: Partial<IDetailsListStyles> = {
  root: {
    '.ms-DetailsRow': {
      border: 0,
    },
    '.ms-DetailsRow-cell': {
      padding: 0,
    },
  },
};

export const detailsListStylesGroupCard = (theme: IH2OTheme): Partial<IDetailsListStyles> => ({
  root: {
    '.ms-GroupedList > .ms-List > .ms-List-surface > .ms-List-page': {
      border: `1px solid ${theme.palette?.gray300}`,
      borderRadius: Sizes.borderRadius,
      marginBottom: DetailsListSizes.GroupPageMarginBottom,
      backgroundColor: theme.palette?.white,
      '.ms-List': {
        paddingLeft: DetailsListSizes.GroupPaddingLeftRight,
        paddingRight: DetailsListSizes.GroupPaddingLeftRight,
        paddingBottom: DetailsListSizes.GroupPaddingBottom,
        '.ms-List-cell': {
          borderBottom: `1px solid ${theme.palette?.gray300}`,
        },
        '.ms-List-cell:hover': {
          borderColor: theme.palette?.primary500,
        },
      },
    },
    '.ms-GroupedList > .ms-List > .ms-List-surface > .ms-List-page:hover': {
      borderColor: theme.palette?.primary500,
    },
  },
});

export const detailsListStylesGroupCartContents = (theme: IH2OTheme): Partial<IDetailsListStyles> =>
  concatStyleSets(detailsListStylesGroupCard(theme), {
    root: {
      '.ms-GroupedList > .ms-List > .ms-List-surface > .ms-List-page': {
        '.ms-List': {
          paddingBottom: DetailsListSizes.GroupPaddingContentsBottom,
          '.ms-List-cell': {
            borderBottom: 0,
            marginBottom: DetailsListSizes.ContentsMarginBottom,
          },
        },
      },
    },
  });

export const detailsListStylesGroup = (theme: IH2OTheme): Partial<IDetailsListStyles> =>
  concatStyleSets(detailsListStylesGroupCard(theme), {
    root: {
      marginBottom: 1,
      '.ms-GroupedList > .ms-List > .ms-List-surface > .ms-List-page': {
        borderRadius: 0,
        marginBottom: -1,
      },
      '.ms-GroupedList > .ms-List > .ms-List-surface > .ms-List-page:hover + .ms-List-page': {
        borderTopColor: theme.palette?.primary500,
      },
    },
  });

export const detailsListStylesBasic = (theme: IH2OTheme): Partial<IDetailsListStyles> => ({
  root: {
    marginBottom: 1,
    '.ms-List > .ms-List-surface > .ms-List-page > .ms-List-cell': {
      border: `1px solid ${theme.palette?.gray300}`,
      backgroundColor: theme.palette?.white,
      marginBottom: -1,
    },
  },
});

export const detailsListStylesCard = (theme: IH2OTheme): Partial<IDetailsListStyles> => ({
  root: {
    '.ms-List > .ms-List-surface > .ms-List-page > .ms-List-cell': {
      border: `1px solid ${theme.palette?.gray300}`,
      backgroundColor: theme.palette?.white,
      borderRadius: Sizes.borderRadius,
      marginBottom: DetailsListSizes.GroupPageMarginBottom,
    },
    '.ms-List > .ms-List-surface > .ms-List-page > .ms-List-cell:hover': {
      borderColor: theme.palette?.primary500,
    },
  },
});

export const groupHeaderStylesDefault = (theme: IH2OTheme): Partial<IGroupHeaderStyles> => ({
  root: {
    borderTop: `solid 1px ${theme.palette?.black_a_8}`,
    background: theme.semanticColors?.bodyBackground,
    '&:hover': {
      background: `${theme.palette?.gray200} 0% 0% no-repeat padding-box`,
    },
  },
  expand: {
    '&:hover': {
      background: theme.palette?.black_a_4,
      cursor: 'pointer',
    },
    '&:active': {
      background: theme.palette?.black_a_8,
    },
  },
});

export const detailsHeaderStylesDefault = (theme: IH2OTheme): Partial<IDetailsHeaderStyles> => ({
  root: {
    borderBottom: 'none',
  },
  cellIsGroupExpander: {
    '&:hover': {
      background: theme.palette?.black_a_4,
      cursor: 'pointer',
    },
    '&:active': {
      background: theme.palette?.black_a_8,
    },
  },
});

export const detailsHeaderStylesBasic = (theme: IH2OTheme): Partial<IDetailsHeaderStyles> => ({
  root: {
    padding: 0,
    height: 48,
    lineHeight: 48,
    backgroundColor: theme.palette?.gray200,
    '.ms-DetailsHeader-cellTitle': {
      paddingLeft: DetailsListSizes.CellLeftPadding,
    },
    '.ms-DetailsHeader-cellTitle .ms-Icon': {
      padding: 0,
      paddingRight: 8,
      top: 0,
      fontSize: 12,
      lineHeight: 47,
    },
    '.ms-DetailsHeader-cellName': {
      fontWeight: FontWeights.semiBold,
      fontSize: 13,
      order: 1, // the order icon should be on the left
    },
    '.ms-DetailsHeader-cell': {
      height: 48,
      lineHeight: 48,
      borderLeft: '1px solid white',
    },
    '.ms-DetailsHeader-cell:first-child': {
      borderLeft: 'none',
    },
    '.is-sortable': { cursor: 'pointer' },
    '.is-sortable[aria-sort="ascending"] .ms-DetailsHeader-cellName .ms-Icon': { display: 'none' },
    '.is-sortable[aria-sort="descending"] .ms-DetailsHeader-cellName .ms-Icon': { display: 'none' },
  },
});

export const detailsHeaderStylesGroup = (theme: IH2OTheme): Partial<IDetailsHeaderStyles> =>
  concatStyleSets(detailsHeaderStylesBasic(theme), {
    root: {
      '.ms-DetailsHeader-cell:first-child': {
        marginLeft: DetailsListSizes.GroupHeaderExpanderWidth,
      },
    },
  });

export const detailsHeaderStylesWhite = (theme: IH2OTheme): Partial<IDetailsHeaderStyles> =>
  concatStyleSets(detailsHeaderStylesBasic(theme), {
    root: {
      backgroundColor: theme.palette?.white,
      marginBottom: 12,
    },
  });

export const detailsRowStylesDefault = (theme: IH2OTheme): Partial<IDetailsRowStyles> => ({
  root: {
    background: theme.semanticColors?.contentBackground,
    borderRadius: Sizes.borderRadius,
    borderBottomColor: theme.semanticColors?.bodyBackground,
    '.is-selected': { borderBottomColor: theme.semanticColors?.bodyBackground },
    '&:hover': {
      background: theme.semanticColors?.contentBackground,
    },
  },
});

export const detailsRowStylesBasic: Partial<IDetailsRowStyles> = {
  root: {
    height: 42,
    lineHeight: 40,
    borderRadius: 0,
  },
  cell: {
    padding: '0px 8px 0px 12px',
  },
};

export const detailsColumnStylesDefault = (theme: IH2OTheme): Partial<IDetailsColumnStyles> => ({
  root: {
    cell: {
      '&:hover': {
        background: theme.semanticColors?.bodyBackground,
      },
      '&:active': {
        background: theme.semanticColors?.bodyBackground,
      },
    },
  },
});
